import { Block } from '@components/Layout';
import { AdvisorHeadshot } from '@components/MiscComponents/AdvisorHeadShot';
import { color, font, query, space } from '@design/templateFns';
import { Email } from '@icon/Email';
import { Phone } from '@icon/Phone';
import { formatPhoneNumber } from '@util/phoneNumber';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Props } from '../BlockMapper';

//rename component from press inquiries to media contact info
export const MediaContactInfo: FC<Props> = (props) => {
  const {
    header,
    description,
    contact1Title,
    contact1Subtitle,
    contact1Phone,
    contact1Name,
    contact1Image,
    contact1Email,
    contact2Title,
    contact2Subtitle,
    contact2Phone,
    contact2Name,
    contact2Image,
    contact2Email,
  } = props;
  const t = useTranslations();
  const [agents, setAgents] = useState<any[]>([]);

  useEffect(() => {
    let agentData = [
      {
        image: contact1Image,
        name: contact1Name,
        title: contact1Title,
        subTitle: contact1Subtitle,
        email: contact1Email,
        phone: contact1Phone,
      },
      {
        image: contact2Image,
        name: contact2Name,
        title: contact2Title,
        subTitle: contact2Subtitle,
        email: contact2Email,
        phone: contact2Phone,
      },
    ];
    setAgents(agentData);
  }, [props]);

  return (
    <Block fullOnMobile backgroundInner={'secondaryBg'}>
      <Wrapper>
        <PressBlock>
          <PressTitle>{header}</PressTitle>
          <PressText>{description}</PressText>
        </PressBlock>
        <AgentsWrapper>
          {agents.length > 0 &&
            agents.map((agent, index) => (
              <AgentBlock key={index}>
                <AdvisorHeadshotWrapper>
                  <AdvisorHeadshot img={agent.image} alt_text={`${agent.name} ${t('Headshot')}`} />
                </AdvisorHeadshotWrapper>
                <AgentInfo>
                  <AgentName>{agent.name}</AgentName>
                  {agent.title && <InfoBlock>{agent.title}</InfoBlock>}
                  {agent.subTitle && <InfoBlock $space={true}>{agent.subTitle}</InfoBlock>}
                  {agent.phone && (
                    <InfoBlock>
                      <a href={`tel:+${agent.phone}`}>
                        <Phone />
                        {formatPhoneNumber(agent.phone.toString())}
                      </a>
                    </InfoBlock>
                  )}
                  {agent.email && (
                    <InfoBlock>
                      <a href={`mailto:${agent.email}`}>
                        <Email />
                        {agent.email}
                      </a>
                    </InfoBlock>
                  )}
                </AgentInfo>
              </AgentBlock>
            ))}
        </AgentsWrapper>
      </Wrapper>
    </Block>
  );
};

export default MediaContactInfo;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space('xl')};
  margin: 0;
  padding: ${space('x10')} ${space('marginMobile')};

  @media screen and (${query.atLeast('tablet')}) {
    gap: ${space('x10')};
    padding: ${space('x10')} ${space('marginTablet')};
  }
  @media screen and (${query.atLeast('desktop')}) {
    grid-template-columns: 1fr 2fr;
    padding: ${space('x10')};
  }
`;

const PressBlock = styled.div`
  @media screen and (${query.below('tablet')}) {
    padding-bottom: ${space('x10')};
  }
`;

const PressTitle = styled.h2`
  ${font('headline-md')};
  margin-top: 0;
  margin-bottom: ${space('md')};
  @media screen and (${query.below('tablet')}) {
    ${font('headline-sm')};
  }
`;

const PressText = styled.p`
  ${font('body-md-light')};
  @media screen and (${query.atLeast('desktop')}) {
    max-width: 350px;
  }
  @media screen and (${query.below('tablet')}) {
    ${font('body-sm-light')};
    padding-bottom: 0;
  }
`;

const AgentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('xl')};

  @media screen and (${query.atLeast('desktop')}) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: ${space('x10')};
  }
`;

const AgentBlock = styled.div`
  flex: 1;
  display: flex;
  gap: ${space('xl')};
  @media screen and (${query.below('tablet')}) {
    gap: ${space('md')};
    padding-bottom: ${space('x6')};
    &:last-child {
      padding-bottom: 0;
    }
  }
`;

const AgentInfo = styled.div`
  width: 100%;
`;

const AgentName = styled.h3`
  ${font('subhead-lg')};
  margin-top: 0;
  margin-bottom: ${space('sm')};
  padding: 0;
  @media screen and (${query.below('tablet')}) {
    ${font('subhead-sm')};
  }
`;
const InfoBlock = styled.div<{ $space?: boolean }>`
  ${font('body-md-light')};
  margin-bottom: ${({ $space }) => ($space ? '15px' : '0px')};
  a {
    color: ${color('gray4')};
    font-weight: 700;
    text-decoration: none;
  }
  svg {
    margin-right: ${space('sm')};
    position: relative;
    top: ${space('xs')};
  }
  svg:last-of-type {
    top: 6px;
  }
  @media screen and (${query.below('tablet')}) {
    ${font('body-sm-light')};
  }
  &:last-child {
    padding-top: ${space('md')};
  }
`;

export const AdvisorHeadshotWrapper = styled.div`
  width: ${space('x10')};
  @media screen and (${query.atLeast('tablet')}) {
    width: 98px; // needs unique size to account for border and box-shadow
  }
`;

// For use in Story
export const StoryWrapper = styled.div`
  display: flex;
`;
